/**
 * A deadline is a positive, finite number of milliseconds — or an explicit, documented exemption.
 *
 * There is deliberately no "no deadline" default. A wait with no bound is how the app ends up on a
 * spinner that never clears, and it is invisible in review: `await somePromise` reads the same
 * whether that promise settles in 2ms or never. Making the bound a required argument moves the
 * question "what if this never settles?" to the moment the code is written.
 *
 * `unboundedWait(reason)` exists for the few waits that are legitimately open-ended: those with no
 * user-visible awaiter (a background tab queueing for a Web Lock, a listener that lives as long as
 * the app), and those on a person in a system sheet that always reports success or cancellation
 * (Sign in with Apple, the Google browser session). The reason is mandatory and greppable; no other
 * user-facing path may use it.
 */
export type Deadline = number | UnboundedWait;

export type UnboundedWait = { readonly reason: string; readonly unbounded: true };

export function unboundedWait(reason: string): UnboundedWait {
  if (reason.trim() === "") {
    throw new Error("unboundedWait requires a reason explaining why no one is left waiting");
  }
  return { reason, unbounded: true };
}

/** The deadline in milliseconds, `null` for an explicit exemption. Rejects anything else. */
export function resolveDeadlineMs(operation: string, deadline: Deadline): number | null {
  if (typeof deadline !== "number") {
    return null;
  }
  if (!Number.isFinite(deadline) || deadline <= 0) {
    throw new RangeError(
      `${operation}: a deadline must be a positive, finite number of milliseconds (got ${deadline}); use unboundedWait(reason) for a documented exemption`,
    );
  }
  return deadline;
}
